import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { showNotification } from '../utils/notifications';
import {
  playRingtone, stopRingtone,
  playOutgoingTone, stopOutgoingTone,
  vibrateCall, stopVibration,
  isAppVisible,
} from '../utils/soundManager';
import { track } from '../utils/firebase';
import { toast } from 'sonner';
import { isNativePlatform, cancelCallNotification } from '../utils/nativePush';

const CALL_TIMEOUT_MS = 30000;

export function useCallManager() {
  const { user, socket } = useAuth();
  const navigate = useNavigate();

  const [callState, setCallState] = useState('idle');
  const [incomingCallData, setIncomingCallData] = useState(null);
  const [outgoingCallData, setOutgoingCallData] = useState(null);
  const timeoutRef = useRef(null);
  const vibIntervalRef = useRef(null);
  const callStateRef = useRef('idle');

  // Keep ref in sync for visibility handler
  useEffect(() => { callStateRef.current = callState; }, [callState]);

  // Refs for accept/reject so SW message handler always uses latest
  const acceptCallRef = useRef(null);
  const rejectCallRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (vibIntervalRef.current) { clearInterval(vibIntervalRef.current); vibIntervalRef.current = null; }
  }, []);

  const resetCall = useCallback(() => {
    clearTimers();
    stopRingtone();
    stopOutgoingTone();
    stopVibration();
    setCallState('idle');
    setIncomingCallData(null);
    setOutgoingCallData(null);
  }, [clearTimers]);

  // ── Visibility change: resume ringtone when tab becomes visible during incoming call ──
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && callStateRef.current === 'incoming') {
        playRingtone();
        vibrateCall();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // ── Service Worker postMessage: handle Accept/Reject from push notification actions ──
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handleSWMessage = (event) => {
      const msg = event.data;
      if (!msg || msg.type !== 'call_action') return;

      if (msg.action === 'reject' && callStateRef.current === 'incoming') {
        if (rejectCallRef.current) rejectCallRef.current();
      }
      if (msg.action === 'accept' && callStateRef.current === 'incoming') {
        if (acceptCallRef.current) acceptCallRef.current();
      }
    };
    navigator.serviceWorker.addEventListener('message', handleSWMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleSWMessage);
  }, []);

  // ── Start outgoing call ──
  const startCall = useCallback((channelId, channelName, isVideo) => {
    if (callState !== 'idle') {
      // Force reset stuck call state instead of silently failing
      console.warn('[CallManager] Call state was', callState, '- forcing reset before new call');
      clearTimers();
      stopRingtone();
      stopOutgoingTone();
      stopVibration();
    }
    setCallState('outgoing');
    setOutgoingCallData({ channel_id: channelId, channel_name: channelName, is_video: isVideo });
    setIncomingCallData(null);
    track.startCall(isVideo ? 'video' : 'audio');

    if (socket) {
      socket.emit('call_started', {
        channel_id: channelId,
        caller_name: user?.name,
        is_video: isVideo,
      });
    }

    playOutgoingTone();
    navigate(`/call/${channelId}?video=${isVideo}&role=caller`);

    timeoutRef.current = setTimeout(() => {
      if (socket) socket.emit('call_missed', { channel_id: channelId });
      stopOutgoingTone();
      toast.info('No answer — call missed');
      setCallState('idle');
      setOutgoingCallData(null);
    }, CALL_TIMEOUT_MS);
  }, [callState, socket, user, navigate, clearTimers]);

  // ── Accept incoming call ──
  const acceptCall = useCallback(() => {
    if (!incomingCallData) return;
    clearTimers();
    stopRingtone();
    stopVibration();

    const { channel_id, is_video } = incomingCallData;

    // Cancel native Android notification (stops ringtone/vibration)
    cancelCallNotification();

    // Close any browser notifications for this call
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.getNotifications({ tag: `call-${channel_id}` }).then((notifications) => {
          notifications.forEach((n) => n.close());
        });
      });
    }

    if (socket) {
      socket.emit('call_accepted', { channel_id });
    }

    setCallState('active');
    setIncomingCallData(null);
    navigate(`/call/${channel_id}?video=${is_video}&role=receiver`);
  }, [incomingCallData, socket, navigate, clearTimers]);

  // ── Reject incoming call ──
  const rejectCall = useCallback(() => {
    if (!incomingCallData) return;
    clearTimers();
    stopRingtone();
    stopVibration();
    cancelCallNotification();

    // Close browser notification
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.getNotifications({ tag: `call-${incomingCallData.channel_id}` }).then((notifications) => {
          notifications.forEach((n) => n.close());
        });
      });
    }

    if (socket) {
      socket.emit('call_rejected', { channel_id: incomingCallData.channel_id });
    }

    setCallState('idle');
    setIncomingCallData(null);
    toast.info('Call declined');
  }, [incomingCallData, socket, clearTimers]);

  // Keep callback refs in sync for the SW message handler
  useEffect(() => { acceptCallRef.current = acceptCall; }, [acceptCall]);
  useEffect(() => { rejectCallRef.current = rejectCall; }, [rejectCall]);

  // ── End active call ──
  const endCall = useCallback((channelId) => {
    clearTimers();
    stopOutgoingTone();
    stopRingtone();
    stopVibration();
    cancelCallNotification();

    if (socket && channelId) {
      socket.emit('call_ended', { channel_id: channelId });
    }

    setCallState('idle');
    setOutgoingCallData(null);
    setIncomingCallData(null);
  }, [socket, clearTimers]);

  // ── Socket event listeners ──
  useEffect(() => {
    if (!socket) return;

    const handleUpdate = (data) => {
      // ── Incoming call ──
      if (data.type === 'incoming_call') {
        if (callStateRef.current !== 'idle') return;

        setCallState('incoming');
        setIncomingCallData(data.data);
        playRingtone();
        vibrateCall();
        vibIntervalRef.current = setInterval(() => vibrateCall(), 2000);

        // Show toast as backup visual indicator
        const callerName = data.data.caller_name || 'Someone';
        const callType = data.data.is_video ? 'Video' : 'Voice';
        toast(`Incoming ${callType} Call from ${callerName}`, {
          duration: 30000,
          important: true,
        });
        showNotification(`Incoming ${callType} Call`, {
          body: `${callerName} is calling you`,
          tag: `call-${data.data.channel_id}`,
          requireInteraction: true,
          data: { type: 'incoming_call', channel_id: data.data.channel_id, is_video: data.data.is_video },
        });

        // 30s timeout → missed
        timeoutRef.current = setTimeout(() => {
          stopRingtone();
          stopVibration();
          if (vibIntervalRef.current) clearInterval(vibIntervalRef.current);
          // Close browser notification
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then((reg) => {
              reg.getNotifications({ tag: `call-${data.data.channel_id}` }).then((ns) => ns.forEach((n) => n.close()));
            });
          }
          setCallState('idle');
          setIncomingCallData(null);
          toast.info(`Missed call from ${callerName}`);
        }, CALL_TIMEOUT_MS);
      }

      // ── Call accepted (caller receives) ──
      if (data.type === 'call_accepted') {
        clearTimers();
        stopOutgoingTone();
        setCallState('active');
        toast.success(`${data.data.accepted_by_name || 'User'} joined the call`);
      }

      // ── Call rejected (caller receives) ──
      if (data.type === 'call_rejected') {
        clearTimers();
        stopOutgoingTone();
        setCallState('idle');
        setOutgoingCallData(null);
        toast.info(`${data.data.rejected_by_name || 'User'} declined the call`);
      }

      // ── Call missed ──
      if (data.type === 'call_missed') {
        clearTimers();
        stopOutgoingTone();
        stopRingtone();
        stopVibration();
        setCallState('idle');
        setOutgoingCallData(null);
        setIncomingCallData(null);
      }

      // ── Call ended ──
      if (data.type === 'call_ended') {
        clearTimers();
        stopRingtone();
        stopOutgoingTone();
        stopVibration();
        setCallState('idle');
        setOutgoingCallData(null);
        setIncomingCallData(null);
        toast.info('Call ended');
        // Navigate away from call screen if still on it
        if (window.location.pathname.startsWith('/call/')) {
          navigate('/', { replace: true });
        }
      }
    };

    socket.on('update', handleUpdate);
    return () => socket.off('update', handleUpdate);
  }, [socket, clearTimers]);

  return {
    callState,
    incomingCallData,
    outgoingCallData,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    resetCall,
  };
}
